import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { circle, point } from '@turf/turf';
import { sizeByZoomLevel } from 'common/utils/util';
import update from 'immutability-helper';
import { RootState } from 'store/store';

interface IUserSlice {
  dataChlorophyll: any;
  dataChlorophyllPolygon: any;
  dataChlorophyllInterval: any;
  isShowAllLayer: boolean;
  dataGeoJsonPoint: GeoJSON.FeatureCollection<GeoJSON.Geometry> | undefined;
  dataGeoJsonPolygon: GeoJSON.FeatureCollection<GeoJSON.Geometry> | undefined;
  zoomLayer: number;
  sizeFixedPoint: number;
  chartDataChlorophyllPreview: any;
}

const initialState: IUserSlice = {
  dataChlorophyll: null,
  dataChlorophyllPolygon: [],
  dataChlorophyllInterval: null,
  isShowAllLayer: true,
  dataGeoJsonPoint: undefined,
  dataGeoJsonPolygon: undefined,
  zoomLayer: 16,
  sizeFixedPoint: 0.0015,
  chartDataChlorophyllPreview: null,
};

export const chlorophyllAnalyticsSlice = createSlice({
  name: 'chlorophyllAnalytic',
  initialState,
  reducers: {
    clearChlorophyllAnalytics: () => {
      return initialState;
    },
    changeDataChlorophyllAnalytics: (state, action: PayloadAction<any>) => {
      state.dataChlorophyll = action.payload;
      if (!state.dataChlorophyllInterval || !action.payload) return;
      state.dataGeoJsonPoint = {
        type: 'FeatureCollection' as any,
        features: action.payload.map((chlorophyllInstance: any) => {
          let color = 'black';
          const chlorophyllInterval = state.dataChlorophyllInterval?.range?.find(
            (item: any, index: number) =>
              item.from <= chlorophyllInstance.chloroIn &&
              (index === state.dataChlorophyllInterval?.range?.length - 1
                ? item.to >= chlorophyllInstance.chloroIn
                : item.to > chlorophyllInstance.chloroIn)
          );
          if (chlorophyllInterval?.visible) {
            color = chlorophyllInterval.color;
          } else color = 'transparent';

          return circle(
            point([Number(chlorophyllInstance.longX || 0), Number(chlorophyllInstance.latY || 0)], {
              ...chlorophyllInstance,
              color,
            }),
            state.sizeFixedPoint,
            { steps: 32 }
          );
        }),
      };
    },
    changeDataChlorophyllInterval: (state, action: PayloadAction<any>) => {
      state.dataChlorophyllInterval = action.payload;
      if (state.dataChlorophyllPolygon && state.dataChlorophyllInterval) {
        const geoJson = {
          type: 'FeatureCollection' as any,
          features: state.dataChlorophyllPolygon.map((chlorophyllInstance: any) => {
            let color = 'black';
            const chlorophyllInterval = state.dataChlorophyllInterval.range?.find(
              (item: any, index: number) =>
                item.from <= chlorophyllInstance.chloroIn &&
                (index === state.dataChlorophyllInterval?.range?.length - 1
                  ? item.to >= chlorophyllInstance.chloroIn
                  : item.to > chlorophyllInstance.chloroIn)
            );
            if (chlorophyllInterval?.visible) {
              color = chlorophyllInterval.color;
            } else color = 'transparent';

            return {
              geometry: { ...chlorophyllInstance.geometry },
              properties: {
                ...chlorophyllInstance.properties,
                color: color,
              },
            };
          }),
        };
        state.dataGeoJsonPolygon = geoJson;
      }
      if (!state.dataChlorophyll || !action.payload) return;
      state.dataGeoJsonPoint = {
        type: 'FeatureCollection' as any,
        features: state.dataChlorophyll.map((chlorophyllInstance: any) => {
          let color = 'black';
          const chlorophyllInterval = action.payload?.range?.find(
            (item: any, index: number) =>
              item.from <= chlorophyllInstance.chloroIn &&
              (index === action.payload?.range?.length - 1
                ? item.to >= chlorophyllInstance.chloroIn
                : item.to > chlorophyllInstance.chloroIn)
          );
          if (chlorophyllInterval?.visible) {
            color = chlorophyllInterval.color;
          } else color = 'transparent';

          return circle(
            point([Number(chlorophyllInstance.longX || 0), Number(chlorophyllInstance.latY || 0)], {
              ...chlorophyllInstance,
              color,
            }),
            state.sizeFixedPoint,
            { steps: 32 }
          );
        }),
      };
    },
    changeVisibleIntervalChlorophyll: (state, action: PayloadAction<number>) => {
      const currentInterval = state.chartDataChlorophyllPreview
        ? state.chartDataChlorophyllPreview.intervalLimit
        : state.dataChlorophyllInterval;
      state.isShowAllLayer = currentInterval.range.every((_item: any, index: number) => {
        if (action.payload === index) {
          return !_item.visible;
        }
        return _item.visible;
      });
      currentInterval.range = update(currentInterval.range, {
        [action.payload]: {
          visible: { $set: !currentInterval.range[action.payload].visible },
        },
      });
      if (state.dataChlorophyll) {
        state.dataGeoJsonPoint = {
          type: 'FeatureCollection' as any,
          features: state.dataChlorophyll.map((vigorInstance: any) => {
            let color = 'black';
            const vigorInterval = currentInterval?.range?.find(
              (item: any, index: number) =>
                item.from <= vigorInstance.chloroIn &&
                (index === currentInterval?.range?.length - 1
                  ? item.to >= vigorInstance.chloroIn
                  : item.to > vigorInstance.chloroIn)
            );
            if (vigorInterval?.visible) {
              color = vigorInterval.color;
            } else color = 'transparent';

            return circle(
              point([Number(vigorInstance.longX || 0), Number(vigorInstance.latY || 0)], {
                ...vigorInstance,
                color,
              }),
              state.sizeFixedPoint,
              { steps: 32 }
            );
          }),
        };
      }
      if (state.dataChlorophyllPolygon) {
        const geoJson = {
          type: 'FeatureCollection' as any,
          features: state.dataChlorophyllPolygon.map((vigorInstance: any) => {
            let color = 'black';
            const vigorInterval = currentInterval?.range?.find(
              (item: any, index: number) =>
                item.from <= vigorInstance.chloroIn &&
                (index === currentInterval?.range?.length - 1
                  ? item.to >= vigorInstance.chloroIn
                  : item.to > vigorInstance.chloroIn)
            );
            if (vigorInterval?.visible) {
              color = vigorInterval.color;
            } else color = 'transparent';
            return {
              geometry: { ...vigorInstance.geometry },
              properties: {
                ...vigorInstance.properties,
                color: color,
              },
            };
          }),
        };
        state.dataGeoJsonPolygon = geoJson;
      }
    },
    changeVisibleIntervalChlorophyllAllLayers: (state, action: PayloadAction<boolean>) => {
      state.isShowAllLayer = action.payload;
      state.dataChlorophyllInterval.range = update(state.dataChlorophyllInterval.range, {
        $apply: (intervalRange: Array<any>) =>
          intervalRange.map((item: any) => {
            return {
              ...item,
              visible: action.payload,
            };
          }),
      });
      if (state.dataChlorophyll) {
        state.dataGeoJsonPoint = {
          type: 'FeatureCollection' as any,
          features: state.dataChlorophyll.map((vigorInstance: any) => {
            let color = 'black';
            const vigorInterval = state.dataChlorophyllInterval?.range?.find(
              (item: any, index: number) =>
                item.from <= vigorInstance.chloroIn &&
                (index === state.dataChlorophyllInterval?.range?.length - 1
                  ? item.to >= vigorInstance.chloroIn
                  : item.to > vigorInstance.chloroIn)
            );
            if (vigorInterval?.visible) {
              color = vigorInterval.color;
            } else color = 'transparent';

            return circle(
              point([Number(vigorInstance.longX || 0), Number(vigorInstance.latY || 0)], {
                ...vigorInstance,
                color,
              }),
              state.sizeFixedPoint,
              { steps: 32 }
            );
          }),
        };
      }
      if (state.dataChlorophyllPolygon) {
        const geoJson = {
          type: 'FeatureCollection' as any,
          features: state.dataChlorophyllPolygon.map((vigorInstance: any) => {
            let color = 'black';
            const vigorInterval = state.dataChlorophyllInterval?.range?.find(
              (item: any, index: number) =>
                item.from <= vigorInstance.chloroIn &&
                (index === state.dataChlorophyllInterval?.range?.length - 1
                  ? item.to >= vigorInstance.chloroIn
                  : item.to > vigorInstance.chloroIn)
            );
            if (vigorInterval?.visible) {
              color = vigorInterval.color;
            } else color = 'transparent';
            return {
              geometry: { ...vigorInstance.geometry },
              properties: {
                ...vigorInstance.properties,
                color: color,
              },
            };
          }),
        };
        state.dataGeoJsonPolygon = geoJson;
      }
    },
    changeDataPolygonChlorophyllAnalytics: (state, action: PayloadAction<any>) => {
      state.dataChlorophyllPolygon = action.payload;
      if (!state.dataChlorophyllInterval) {
        return;
      }
      const geoJson = {
        type: 'FeatureCollection' as any,
        features: action.payload.map((chlorophyllInstance: any) => {
          let color = 'black';
          const chlorophyllInterval = state.dataChlorophyllInterval.range?.find(
            (item: any, index: number) =>
              item.from <= chlorophyllInstance.chloroIn &&
              (index === state.dataChlorophyllInterval?.range?.length - 1
                ? item.to >= chlorophyllInstance.chloroIn
                : item.to > chlorophyllInstance.chloroIn)
          );
          if (chlorophyllInterval?.visible) {
            color = chlorophyllInterval.color;
          } else color = 'transparent';

          return {
            geometry: { ...chlorophyllInstance.geometry },
            properties: {
              ...chlorophyllInstance.properties,
              color: color,
            },
          };
        }),
      };
      state.dataGeoJsonPolygon = geoJson;
    },

    changeZoomLayerChlorophyll: (state, action: PayloadAction<number>) => {
      state.zoomLayer = action.payload;
      const zoomLevel = action.payload;
      const currentRange = state.chartDataChlorophyllPreview
        ? state.chartDataChlorophyllPreview.intervalLimit?.range
        : state.dataChlorophyllInterval?.range;
      if (state.dataChlorophyll) {
        state.dataGeoJsonPoint = {
          type: 'FeatureCollection' as any,
          features: state.dataChlorophyll.map((vigorInstance: any) => {
            let color = 'black';
            const vigorInterval = currentRange?.find(
              (item: any, index: number) =>
                item.from <= vigorInstance.chloroIn &&
                (index === currentRange?.length - 1
                  ? item.to >= vigorInstance.chloroIn
                  : item.to > vigorInstance.chloroIn)
            );
            if (vigorInterval?.visible) {
              color = vigorInterval.color;
            } else color = 'transparent';

            return circle(
              point([Number(vigorInstance.longX || 0), Number(vigorInstance.latY || 0)], {
                ...vigorInstance,
                color,
              }),
              sizeByZoomLevel(zoomLevel),
              { steps: 32 }
            );
          }),
        };
      }
    },
    changeFixedSizePointChlorophyll: (state, action: PayloadAction<any>) => {
      state.sizeFixedPoint = action.payload;
    },
    changeChartDataChlorophyllPreview: (state, action: PayloadAction<any>) => {
      if (action.payload) {
        const intervalLimit = {
          ...state.dataChlorophyllInterval,
          range: action.payload.range,
        };
        const chartData: any = [];
        action.payload.range.forEach((item: any) => {
          const listByRange = state.dataChlorophyll.filter(
            (chlorophyll: any) => chlorophyll.chloroIn >= item.from && chlorophyll.chloroIn <= item.to
          );
          chartData.push({ [item.lable]: listByRange.length });
        });
        let dataPreview = { chartData, intervalLimit };

        state.chartDataChlorophyllPreview = dataPreview;

        state.dataGeoJsonPoint = {
          type: 'FeatureCollection' as any,
          features: state.dataChlorophyll.map((vigorInstance: any) => {
            let color = 'black';
            const vigorInterval = state.chartDataChlorophyllPreview?.intervalLimit?.range?.find(
              (item: any, index: number) =>
                item.from <= vigorInstance.chloroIn &&
                (index === state.chartDataChlorophyllPreview?.intervalLimit?.range?.length - 1
                  ? item.to >= vigorInstance.chloroIn
                  : item.to > vigorInstance.chloroIn)
            );
            if (vigorInterval?.visible) {
              color = vigorInterval.color;
            } else color = 'transparent';

            return circle(
              point([Number(vigorInstance.longX || 0), Number(vigorInstance.latY || 0)], {
                ...vigorInstance,
                color,
              }),
              state.sizeFixedPoint,
              { steps: 32 }
            );
          }),
        };
        state.dataGeoJsonPolygon = {
          type: 'FeatureCollection' as any,
          features: state.dataChlorophyllPolygon.map((chlorophyllInstance: any) => {
            let color = 'black';
            const chlorophyllInterval = state.chartDataChlorophyllPreview?.intervalLimit?.range?.find(
              (item: any, index: number) =>
                item.from <= chlorophyllInstance.chloroIn &&
                (index === state.chartDataChlorophyllPreview?.intervalLimit?.range?.length - 1
                  ? item.to >= chlorophyllInstance.chloroIn
                  : item.to > chlorophyllInstance.chloroIn)
            );
            if (chlorophyllInterval?.visible) {
              color = chlorophyllInterval.color;
            } else color = 'transparent';

            return {
              geometry: { ...chlorophyllInstance.geometry },
              properties: {
                ...chlorophyllInstance.properties,
                color: color,
              },
            };
          }),
        };
      } else {
        state.chartDataChlorophyllPreview = null;
      }
    },
  },
});

export const {
  clearChlorophyllAnalytics,
  changeDataChlorophyllAnalytics,
  changeDataChlorophyllInterval,
  changeVisibleIntervalChlorophyll,
  changeVisibleIntervalChlorophyllAllLayers,
  changeDataPolygonChlorophyllAnalytics,
  changeZoomLayerChlorophyll,
  changeFixedSizePointChlorophyll,
  changeChartDataChlorophyllPreview,
} = chlorophyllAnalyticsSlice.actions;

export const chlorophyllAnalyticsSelector = (state: RootState) => state.chlorophyllAnalytic;

export default chlorophyllAnalyticsSlice.reducer;
